import React, { useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { Combobox } from "@/components/ui/combobox";
import { SiteHeader } from "@/components/site-header";
import { EmptyState } from "@/components/ui/empty-state";
import { ButtonGroup } from "@/components/ui/button-group";
import {
  Search,
  SearchX,
  Shield,
  ShieldCheck,
  UserCog,
  UserX,
  UserCheck,
  UserPlus,
  Users,
  BarChart3,
  Workflow,
  Bot,
  Book,
  CreditCard,
  Phone,
  Lock,
  ShieldAlert,
  Wrench,
  History,
  AlertCircle,
  CheckCircle2,
  Ban,
  Mail,
  Pencil,
  Trash2,
  UsersRound,
  Building2,
  User,
  Copy,
  Check,
  FolderOpen,
  MessageSquare,
  ClipboardCheck,
  Puzzle
} from 'lucide-react';
import { useEnhancedToast } from "@/components/ui/enhanced-toast";
// import { toast } from "@/hooks/use-toast";
import { PlatformMembersLoading } from './components/PlatformMembersLoading';
import { EditRoleDialog } from './components/EditRoleDialog';
import { AddMembersDialog } from './components/AddMembersDialog';
import { RemoveMemberDialog } from './components/RemoveMemberDialog';
import type { UserDetails, MemberPermissions, Roles } from '@/api/users/users.types';
import cn from 'classnames';
import { HoverCard, HoverCardTrigger, HoverCardContent } from "@/components/ui/hover-card";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import { getUsers, removeMember, updateMember, resendInvite } from "@/api";
import { useAuth } from '@/context/AuthContext';

// Role display mapping
type RoleKey = Roles | "super_admin" | "tenant_admin";
const roleDisplayMap: Record<RoleKey, { label: string, color: string, icon: typeof Shield }> = {
  super_admin: {
    label: "Super Admin",
    color: "bg-red-50 text-red-700 border-red-200/80 dark:bg-red-500/10 dark:text-red-400 dark:border-red-500/20",
    icon: ShieldAlert
  },
  tenant_admin: {
    label: "Tenant Admin",
    color: "bg-orange-50 text-orange-700 border-orange-200/80 dark:bg-orange-500/10 dark:text-orange-400 dark:border-orange-500/20",
    icon: Building2
  },
  company_admin: {
    label: "Company Admin",
    color: "bg-purple-50 text-purple-700 border-purple-200/80 dark:bg-purple-500/10 dark:text-purple-400 dark:border-purple-500/20",
    icon: ShieldCheck
  },
  team_member: {
    label: "Team Member",
    color: "bg-sky-50 text-sky-700 border-sky-200/80 dark:bg-sky-500/10 dark:text-sky-400 dark:border-sky-500/20",
    icon: Shield
  }
};

// Permission display mapping - Icons match the sidebar icons from routes.tsx for consistency
const permissionDisplayMap: Record<string, { label: string, description: string, icon: any }> = {
  analytics: {
    label: "Analytics",
    description: "View analytics and reports",
    icon: BarChart3
  },
  rubrics: {
    label: "Rubrics",
    description: "Manage analytics rubrics and evaluation criteria",
    icon: ClipboardCheck
  },
  workflows: {
    label: "Workflows",
    description: "Access to workflow management",
    icon: Workflow
  },
  assistant: {
    label: "Assistants",
    description: "Access to AI assistant features",
    icon: Bot
  },
  phone_numbers: {
    label: "Phone Numbers",
    description: "Manage phone numbers",
    icon: Phone
  },
  tools: {
    label: "Tools",
    description: "Manage custom tools",
    icon: Wrench
  },
  files: {
    label: "Files",
    description: "Manage uploaded files",
    icon: FolderOpen
  },
  knowledge_base: {
    label: "Knowledge Base",
    description: "Access to knowledge base",
    icon: Book
  },
  multi_agent: {
    label: "Multi Agent",
    description: "Manage team Multi Agents",
    icon: UsersRound
  },
  messaging: {
    label: "Messaging",
    description: "Manage WhatsApp and messaging services",
    icon: MessageSquare
  },
  widgets: {
    label: "Widgets",
    description: "Create and manage widgets",
    icon: Puzzle
  },
  calls: {
    label: "Call Logs",
    description: "View call logs and recordings",
    icon: History
  },
  team_management: {
    label: "Team Management",
    description: "Manage team members",
    icon: Building2
  },
  billing: {
    label: "Billing",
    description: "View billing and invoices",
    icon: CreditCard
  }
};

const getPermissionColor = (permission: string) => {
  switch (permission) {
    case "EDITOR":
      return "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/50 dark:text-emerald-400 dark:border-emerald-800/20";
    case "VIEWER":
      return "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/50 dark:text-blue-400 dark:border-blue-800/20";
    case "RESTRICTED":
      return "bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-950/50 dark:text-rose-400 dark:border-rose-800/20";
    default:
      return "bg-slate-50 text-slate-700 border-slate-200 dark:bg-slate-950/50 dark:text-slate-400 dark:border-slate-800/20";
  }
};

const formatRole = (role: Roles) => {
  const roleInfo = roleDisplayMap[role as RoleKey] || {
    label: role.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' '),
    color: "bg-gray-50 text-gray-700 border-gray-200/80 dark:bg-gray-500/10 dark:text-gray-400 dark:border-gray-500/20",
    icon: Shield
  };

  return (
    <div className={cn(
      "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border",
      roleInfo.color
    )}>
      <roleInfo.icon className="h-3.5 w-3.5" />
      {roleInfo.label}
    </div>
  );
};

const formatPermissions = (permissions: MemberPermissions) => {
  if (!permissions) return null;

  // Define consistent order for permissions (same as sidebar routes.tsx order)
  const permissionOrder = [
    'analytics',       // Analytics
    'rubrics',         // Rubrics
    'workflows',       // Workflows
    'assistant',       // Assistants
    'phone_numbers',   // Phone Numbers
    'tools',           // Tools
    'files',           // Files
    'knowledge_base',  // Knowledge Base
    'multi_agent',     // Multi Agent
    'messaging',       // Messaging
    'widgets',         // Widgets
    'calls',           // Call Logs
    'team_management', // Team Management
    'billing'          // Billing
  ];

  // Sort permissions by the defined order
  const sortedPermissions = Object.entries(permissions).sort(([keyA], [keyB]) => {
    const indexA = permissionOrder.indexOf(keyA);
    const indexB = permissionOrder.indexOf(keyB);
    return (indexA === -1 ? 999 : indexA) - (indexB === -1 ? 999 : indexB);
  });

  return (
    <div className="flex flex-wrap gap-1.5">
      {sortedPermissions.map(([key, permission]) => {
        const permInfo = permissionDisplayMap[key];
        if (!permInfo) return null;

        return (
          <HoverCard key={key}>
            <HoverCardTrigger>
              <div className={cn(
                "inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border cursor-help",
                getPermissionColor(permission)
              )}>
                <permInfo.icon className="h-3 w-3" />
                <span className="hidden sm:inline">{permInfo.label}</span>
                <span className="text-[10px] font-normal opacity-80">({permission.charAt(0)})</span>
              </div>
            </HoverCardTrigger>
            <HoverCardContent className="w-64">
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <div className="flex h-8 w-8 items-center justify-center rounded-md bg-primary/10">
                    <permInfo.icon className="h-4 w-4 text-primary" />
                  </div>
                  <h4 className="text-sm font-semibold">{permInfo.label}</h4>
                </div>
                <p className="text-xs text-muted-foreground">{permInfo.description}</p>
                <div className={cn(
                  "inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium border mt-2",
                  getPermissionColor(permission)
                )}>
                  <span>Access Level: {permission}</span>
                </div>
              </div>
            </HoverCardContent>
          </HoverCard>
        );
      })}
    </div>
  );
};

// Helper function for focus and overlay cleanup
const cleanupDOMAfterDialog = () => {
  // Reset focus to body if nothing else is focused
  if (!document.activeElement || document.activeElement === document.body) {
    // Find a safe element to focus
    const safeElement = document.querySelector('main') || document.querySelector('body');
    if (safeElement instanceof HTMLElement) {
      safeElement.setAttribute('tabindex', '-1');
      safeElement.focus();
      // Remove tabindex after focus
      setTimeout(() => safeElement.removeAttribute('tabindex'), 100);
    }
  }
  
  // Clean up Radix UI specific elements
  
  // 1. Fix Radix Popper content wrappers with aria-hidden
  const popperContentWrappers = document.querySelectorAll('[data-radix-popper-content-wrapper]');
  popperContentWrappers.forEach(wrapper => {
    if (wrapper instanceof HTMLElement) {
      // Check if it has aria-hidden but still contains focused elements
      if (wrapper.hasAttribute('aria-hidden')) {
        const hasFocusedDescendant = wrapper.contains(document.activeElement);
        if (hasFocusedDescendant) {
          // Remove focus from descendants
          if (document.activeElement instanceof HTMLElement) {
            document.activeElement.blur();
          }
        }
        // Remove aria-hidden completely rather than setting to false
        wrapper.removeAttribute('aria-hidden');
        wrapper.removeAttribute('data-aria-hidden');
      }
    }
  });
  
  // 2. General cleanup for any aria-hidden elements
  const ariaHiddenElements = document.querySelectorAll('[aria-hidden="true"], [data-aria-hidden="true"]');
  ariaHiddenElements.forEach(element => {
    if (element instanceof HTMLElement) {
      // Check if element or any of its children has focus
      if (element.contains(document.activeElement)) {
        if (document.activeElement instanceof HTMLElement) {
          document.activeElement.blur();
        }
      }
      
      // Remove the attribute after ensuring no focus issues
      element.removeAttribute('aria-hidden');
      element.removeAttribute('data-aria-hidden');
    }
  });
  
  // 3. Clean up any Radix portal elements that aren't being used
  const radixPortals = document.querySelectorAll('[data-radix-portal]');
  radixPortals.forEach(portal => {
    if (portal instanceof HTMLElement && !portal.children.length) {
      portal.remove(); // Remove empty portals
    }
  });
  
  // Ensure body is interactive
  document.body.style.pointerEvents = '';
  document.body.style.overflow = '';
  
  // Force layout recalculation to ensure DOM is updated
  document.body.offsetHeight;
};

const PlatformMembers = () => {
  const [isLoading, setIsLoading] = React.useState(true);
  const [editDialogOpen, setEditDialogOpen] = React.useState(false);
  const [removeDialogOpen, setRemoveDialogOpen] = React.useState(false);
  const [addMembersDialogOpen, setAddMembersDialogOpen] = React.useState(false);
  const [selectedMember, setSelectedMember] = React.useState<UserDetails | null>(null);
  const [members, setMembers] = React.useState<UserDetails[]>([]);
  const [searchQuery, setSearchQuery] = React.useState("");
  const [roleFilter, setRoleFilter] = React.useState<string>("");
  const [statusFilter, setStatusFilter] = React.useState<string>("");
  const [resendingInvite, setResendingInvite] = React.useState<string | null>(null);
  const [copiedEmail, setCopiedEmail] = React.useState<string | null>(null);
  const { user } = useAuth();
  const { error, success } = useEnhancedToast();

  // Create refs for toast functions
  const errorRef = useRef(error);
  const successRef = useRef(success);

  // Update refs when toast functions change
  useEffect(() => {
      errorRef.current = error;
      successRef.current = success;
  }, [error, success]);

  const canManageTeam = user?.permissions?.team_management === 'EDITOR';

  const fetchMembers = React.useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await getUsers();
      if (response.data) {
        setMembers(response.data);
      } else {
        errorRef.current({
          icon: AlertCircle,
          title: "Failed to Load Members",
          description: response.error || "Could not fetch team members",
          duration: 4000,
        });
      }
    } catch (err) {
      errorRef.current({
        icon: AlertCircle,
        title: "Error Loading Members",
        description: err instanceof Error ? err.message : "Could not fetch team members",
        duration: 4000,
      });
    } finally {
      setIsLoading(false);
    }
  }, []);

  React.useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  const filteredMembers = React.useMemo(() => {
    return members.filter(member => {
      // Search filter
      const matchesSearch = 
        member.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
        `${member.first_name} ${member.last_name}`.toLowerCase().includes(searchQuery.toLowerCase());
      
      // Role filter
      const matchesRole = !roleFilter || member.role === roleFilter;
      
      // Status filter
      const matchesStatus = !statusFilter || member.status === statusFilter;
      
      return matchesSearch && matchesRole && matchesStatus;
    });
  }, [members, searchQuery, roleFilter, statusFilter]);

  const handleRoleSave = async (role: Roles, permissions: MemberPermissions) => {
    if (!selectedMember) return;

    try {
      const response = await updateMember({
        user_id: selectedMember.user_id,
        role: role,
        permissions
      });

      if (response.error) {
        errorRef.current({
          icon: AlertCircle,
          title: "Update Failed",
          description: response.error,
          duration: 4000,
        });
        return;
      }

      // Refresh the members list
      await fetchMembers();

      successRef.current({
        icon: CheckCircle2,
        title: "Role Updated",
        description: "Member role and permissions have been updated successfully",
        duration: 4000,
      });

      setEditDialogOpen(false);
    } catch (err) {
      errorRef.current({
        icon: AlertCircle,
        title: "Update Failed",
        description: err instanceof Error ? err.message : "Could not update member role",
        duration: 4000,
      });
    }
  };

  const handleRemoveMember = async (member: UserDetails): Promise<void> => {
    try {
      // Close dialog first to properly release focus before making API call
      setRemoveDialogOpen(false);
      
      // Add a small delay to ensure focus is properly released before API call
      await new Promise(resolve => setTimeout(resolve, 50));
      
      // Clean up any stray elements
      cleanupDOMAfterDialog();
      
      const response = await removeMember(member.user_id);

      if (response.error) {
        throw new Error(response.error);
      }
      
      // Then handle the successful response
      setTimeout(async () => {
        // Refresh the members list
        await fetchMembers();

        successRef.current({
          icon: CheckCircle2,
          title: "Member Removed",
          description: "Team member has been removed successfully",
          duration: 4000,
        });
        
        // Clean up again after success
        setTimeout(cleanupDOMAfterDialog, 100);
      }, 100);
    } catch (err) {
      errorRef.current({
        icon: AlertCircle,
        title: "Remove Failed",
        description: err instanceof Error ? err.message : "Could not remove team member",
        duration: 4000,
      });
    } finally {
      // Make sure member selection is cleared to avoid any stale references
      setSelectedMember(null);
      
      // Force blur any active element to ensure no element retains focus
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
      
      // Final cleanup
      cleanupDOMAfterDialog();
    }
  };

  const handleResendInvite = async (userId: string, e?: React.MouseEvent) => {
    // Prevent default event to keep dropdown open
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    try {
      setResendingInvite(userId);
      const response = await resendInvite({ user_id: userId });
      
      if (response.error) {
        throw new Error(response.error);
      }
      
      successRef.current({
        icon: Mail,
        title: "Invitation Sent",
        description: "Invitation has been resent successfully",
        duration: 4000,
      });
    } catch (err) {
      errorRef.current({
        icon: AlertCircle,
        title: "Failed to Resend",
        description: err instanceof Error ? err.message : "Could not resend invitation",
        duration: 4000,
      });
    } finally {
      setResendingInvite(null);
    }
  };

  // Add a component cleanup effect
  useEffect(() => {
    return () => {
      // Clean up when component unmounts
      cleanupDOMAfterDialog();
    };
  }, []);
  
  // Cleanup after dialogs close
  useEffect(() => {
    if (!editDialogOpen && !removeDialogOpen) {
      // Use a timeout to ensure focus is properly released
      const timer = setTimeout(() => {
        setSelectedMember(null);
        cleanupDOMAfterDialog();
        
        // Run another cleanup after animation completes
        setTimeout(cleanupDOMAfterDialog, 300);
      }, 200);
      
      return () => clearTimeout(timer);
    }
  }, [editDialogOpen, removeDialogOpen]);

  if (isLoading) {
    return <PlatformMembersLoading />;
  }

  // Filter options
  const roleOptions = [
    { label: "All Roles", value: "" },
    { label: "Company Admin", value: "company_admin" },
    { label: "Team Member", value: "team_member" },
  ];

  const statusOptions = [
    { label: "All Status", value: "" },
    { label: "Active", value: "active" },
    { label: "Inactive", value: "inactive" },
  ];

  const activeFilterCount = (roleFilter ? 1 : 0) + (statusFilter ? 1 : 0);

  const handleClearFilters = () => {
    setRoleFilter("");
    setStatusFilter("");
    setSearchQuery("");
  };

  return (
    <div className="flex flex-col h-full">
      <SiteHeader
        title="Team Management"
        subtitle="Manage team members with role-based access control"
      >
        {/* Search */}
        <div className="relative w-[250px] lg:w-[300px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          <Input
            placeholder="Search by name or email..."
            className="h-9 pl-9"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        {/* Role Filter */}
        <Combobox
          options={roleOptions}
          value={roleFilter}
          onValueChange={setRoleFilter}
          placeholder="Filter by role..."
          className="w-[180px]"
        />

        {/* Status Filter */}
        <Combobox
          options={statusOptions}
          value={statusFilter}
          onValueChange={setStatusFilter}
          placeholder="Filter by status..."
          className="w-[180px]"
        />

        {/* Add Members Button */}
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <div>
                <Button 
                  size="sm"
                  onClick={() => canManageTeam && setAddMembersDialogOpen(true)}
                  disabled={!canManageTeam}
                  className="gap-2"
                >
                  <UserPlus className="h-4 w-4" />
                  Add Members
                  {!canManageTeam && <Lock className="w-3.5 h-3.5 opacity-70" />}
                </Button>
              </div>
            </TooltipTrigger>
            {!canManageTeam && (
              <TooltipContent className="flex items-center gap-2 max-w-xs">
                <ShieldAlert className="w-12 h-12 text-amber-500" />
                <p>You need editor permissions to add team members. Contact your administrator for access.</p>
              </TooltipContent>
            )}
          </Tooltip>
        </TooltipProvider>
      </SiteHeader>

      <div className="flex-1 overflow-auto">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.15 }}
          className="px-4 py-6 md:px-8 md:py-8"
        >
          {/* Team Members Table */}
          {filteredMembers.length === 0 ? (
            members.length === 0 ? (
              // No members at all
              <EmptyState
                icon={Users}
                title="No Team Members Yet"
                description="Get started by inviting team members to collaborate"
                action={{
                  label: "Add Members",
                  onClick: () => canManageTeam && setAddMembersDialogOpen(true),
                }}
              />
            ) : (
              // No matching results
              <div 
                className="flex items-center justify-center text-center"
                style={{ height: 'calc(100vh - 200px)' }}
              >
                <div className="flex flex-col items-center">
                  <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-muted mb-4">
                    <SearchX className="h-8 w-8 text-muted-foreground" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">No Matching Members</h3>
                  <p className="text-sm text-muted-foreground mb-6 max-w-sm">
                    No team members found matching your search and filter criteria. Try adjusting your filters.
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleClearFilters}
                    className="gap-2"
                  >
                    <Ban className="h-4 w-4" />
                    Clear All Filters
                  </Button>
                </div>
              </div>
            )
          ) : (
            <div className="rounded-md border shadow-sm">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead className="w-[280px]">User</TableHead>
                    <TableHead className="w-[180px]">Role</TableHead>
                    <TableHead>Permissions</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="w-[120px] text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredMembers.map((member) => {
                    // Check if user has signed up (has first_name and last_name)
                    const hasSignedUp = member.first_name && member.last_name;
                    const initials = hasSignedUp 
                      ? `${member.first_name[0]}${member.last_name[0]}`.toUpperCase()
                      : null;
                    
                    return (
              <TableRow key={member.user_id} className="group">
                <TableCell>
                  <div className="flex items-center gap-3">
                    <Avatar className="h-9 w-9">
                      {/* <AvatarImage src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${member.email}`} /> */}
                      <AvatarFallback className={cn(
                        "text-xs font-semibold",
                        hasSignedUp 
                          ? "bg-primary/10 text-primary" 
                          : "bg-muted text-muted-foreground"
                      )}>
                        {hasSignedUp ? (
                          initials
                        ) : (
                          <User className="h-4 w-4" />
                        )}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex flex-col min-w-0">
                      {hasSignedUp ? (
                        <span className="font-medium truncate">
                          {member.first_name} {member.last_name}
                        </span>
                      ) : (
                        <span className="text-sm italic text-muted-foreground truncate">
                          Pending signup
                        </span>
                      )}
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-muted-foreground truncate">
                          {member.email}
                        </span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-5 w-5 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigator.clipboard.writeText(member.email);
                            setCopiedEmail(member.user_id);
                            setTimeout(() => setCopiedEmail(null), 2000);
                          }}
                        >
                          {copiedEmail === member.user_id ? (
                            <Check className="h-3 w-3 text-green-500" />
                          ) : (
                            <Copy className="h-3 w-3" />
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  {formatRole(member.role)}
                </TableCell>
                <TableCell>
                  {formatPermissions(member.permissions)}
                </TableCell>
                <TableCell>
                  <div className={cn(
                    "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border",
                    member.status === 'active' 
                      ? "bg-green-50 text-green-700 border-green-200 dark:bg-green-950/50 dark:text-green-400 dark:border-green-800/20"
                      : "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/50 dark:text-amber-400 dark:border-amber-800/20"
                  )}>
                    {member.status === 'active' ? (
                      <>
                        <UserCheck className="h-3.5 w-3.5" />
                        Active
                      </>
                    ) : (
                      <>
                        <UserX className="h-3.5 w-3.5" />
                        Pending
                      </>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  {canManageTeam && member.email !== user?.email && 
                    !(user?.role === "team_member" && member.role === "company_admin") &&
                    !(user?.role === "manager" && (member.role === "company_admin" || member.role === "tenant_admin")) ? (
                      <div className="flex items-center justify-end">
                        <ButtonGroup>
                          {/* Resend Invite Button - Only for inactive members */}
                          {member.status !== 'active' && (
                            <TooltipProvider delayDuration={200}>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="outline"
                                    size="icon"
                                    className="h-8 w-8"
                                    onClick={(e) => handleResendInvite(member.user_id, e)}
                                    disabled={resendingInvite === member.user_id}
                                  >
                                    {resendingInvite === member.user_id ? (
                                      <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                                    ) : (
                                      <Mail className="h-4 w-4" />
                                    )}
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>Resend Invitation</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                          
                          {/* Edit Button */}
                          <TooltipProvider delayDuration={200}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="outline"
                                  size="icon"
                                  className="h-8 w-8"
                                  onClick={() => {
                                    if (document.activeElement instanceof HTMLElement) {
                                      document.activeElement.blur();
                                    }
                                    setTimeout(() => {
                                      setSelectedMember(member);
                                      setEditDialogOpen(true);
                                    }, 50);
                                  }}
                                >
                                  <Pencil className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Edit Role & Permissions</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>

                          {/* Delete Button */}
                          <TooltipProvider delayDuration={200}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="outline"
                                  size="icon"
                                  className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    if (document.activeElement instanceof HTMLElement) {
                                      document.activeElement.blur();
                                    }
                                    setSelectedMember(member);
                                    setTimeout(() => {
                                      setRemoveDialogOpen(true);
                                    }, 150);
                                  }}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Remove Member</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </ButtonGroup>
                      </div>
                  ) : null}
                </TableCell>
              </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}

          <EditRoleDialog
            open={editDialogOpen}
            onOpenChange={setEditDialogOpen}
            member={selectedMember}
            onSave={handleRoleSave}
          />

          <RemoveMemberDialog
            open={removeDialogOpen}
            onOpenChange={setRemoveDialogOpen}
            member={selectedMember}
            onConfirm={handleRemoveMember}
          />

          <AddMembersDialog
            open={addMembersDialogOpen}
            onOpenChange={setAddMembersDialogOpen}
            onSuccess={fetchMembers}
            existingMembers={members}
          />
        </motion.div>
      </div>
    </div>
  );
};

export default PlatformMembers;
